import { v3, v2, quat } from "oc-threejs/short3";
import * as Textures from "oc-threejs/Textures";
import * as Scenes from "oc-threejs/Scenes";
import { Connector } from "./Connector";

import {
  ArrowHelper,
  Color,
  CylinderGeometry,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  TorusGeometry,
} from "three";

// import dat from 'dat-gui';

/**
 * Visualisierung eines Connectors
 */
export class ConnectorHelper extends Object3D {
  connector: Connector;
  debugConnectedMarker: Mesh | null = null;
  torus: Mesh[] | null = null;

  // name property is in Object3D

  /**
   * Creates an instance of ConnectorHelper.
   * @param {Connector} connector
   */
  constructor(connector: Connector) {
    super();
    this.connector = connector;

    this.name = this.figure + "Helper";

    if (STAGE.settings.showDebugObjects) {
      this.addDebugObjects();
    }

    if (this.figure != "Anchor") {
      this.addPulseRings();
    }

    this.hideTori();
  }

  addDebugObjects(): void {
    this.add(new ArrowHelper(v3(1, 0, 0), v3(0, 0, 0), this.size, 0xff0000));
    this.add(new ArrowHelper(v3(0, 1, 0), v3(0, 0, 0), this.size, 0x00ff00));
    this.add(new ArrowHelper(v3(0, 0, 1), v3(0, 0, 0), this.size, 0x0000ff));
    this.addIf(this.figureBox());
    this.debugConnectedMarker = new Mesh(
      new TorusGeometry(1.5 * this.size, 0.3 * this.size, 10, 16),
      new MeshBasicMaterial({
        color: 0xf0f000,
        side: DoubleSide,
      }),
    );
    this.add(this.debugConnectedMarker);
    this.connector.on("connect", () => this.showConnected(true));
    this.connector.on("disconnect", () => this.showConnected(false));

    this.showConnected(false);
  }

  get size(): number {
    return STAGE.settings.connectors.size;
  }

  get markerSettings(): any {
    return STAGE.settings.connectors.markers;
  }

  addIf(child: Object3D | null | undefined): void {
    if (child) {
      this.add(child);
    }
  }

  showConnected(on: boolean): void {
    if (this.debugConnectedMarker) {
      this.debugConnectedMarker.visible = on;
      Textures.setOpacity(this.debugConnectedMarker, on ? 1 : 0);
    }
  }

  get figure(): string {
    return this.connector.type.figure;
  }

  figureBox(): Object3D | null {
    switch (this.figure) {
      case "Anchor": {
        const box = Scenes.box(v3(3 * this.size, 0.3 * this.size, 3 * this.size), {
          color: 0x404040,
        } as any); // Scenes.box options
        box.position.set(0 * this.size, 0 * this.size, 0 * this.size);
        return box;
      }
      case "Magnet": {
        const box = Scenes.box(v3(2 * this.size, 2 * this.size, 1 * this.size), {
          color: 0x808080,
        } as any);
        box.position.set(0 * this.size, 0 * this.size, -0.5 * this.size);
        return box;
      }
      case "Eyelet":
        return new Mesh(
          new TorusGeometry(1 * this.size, 0.2 * this.size, 10, 16),
          new MeshBasicMaterial({
            color: 0xb00000,
            side: DoubleSide,
          }),
        );
      case "Hook":
        return new Mesh(
          new CylinderGeometry(0 * this.size, 1 * this.size, 2 * this.size, 16, 1),
          new MeshBasicMaterial({
            color: 0x00b000,
            side: DoubleSide,
          }),
        ).rotateX((90 * Math.PI) / 180);
    }
    return null;
  }

  addPulseRings(): void {
    this.torus = [];
    const tori = this.markerSettings.tori;
    for (let i = 0; i < tori; i++) {
      this.torus.push(
        this.newTorus(
          this.markerSettings.radius * (i + 1),
          this.markerSettings.tube * (tori - i + 1),
          0xf0f000,
        ),
      );
    }
    this.torus.forEach((t) => this.add(t));
  }

  onAnimatorTick(now: number, tags: Set<string>): void {
    if (!this.torus) {
      return;
    }
    if (tags.has("drag")) {
      this.torus.forEach((torus, i) => {
        if (this.connector.pulseStrength > 0.1) {
          const opacity =
            Math.sin(
              ((i * this.markerSettings.alpha + now * this.markerSettings.speed) * Math.PI) / 180,
            ) *
            (1 - i * this.markerSettings.ab);
          const s = this.connector.pulseStrength;
          if (s > 1 - Connector.EPSILON) {
            (torus.material as MeshBasicMaterial).opacity = opacity;
            (torus.material as MeshBasicMaterial).color = new Color(0xaaff00);
          } else {
            (torus.material as MeshBasicMaterial).opacity = opacity * s;
            (torus.material as MeshBasicMaterial).color = new Color(0xffff00);
          }
          (torus.material as MeshBasicMaterial).needsUpdate = true;
          torus.visible = true;
        } else {
          torus.visible = false;
        }
      });
    } else {
      this.hideTori();
    }
  }

  hideTori(): void {
    if (!this.torus) {
      return;
    }
    this.torus.forEach((torus) => {
      Textures.setOpacity(torus, 0);
      torus.visible = false;
    });
  }

  newTorus(r: number, s: number, color: number): Mesh {
    return new Mesh(
      new TorusGeometry(
        r,
        s,
        this.markerSettings.radialSegments,
        this.markerSettings.tubularSegments,
      ),
      new MeshBasicMaterial({
        color,
        side: DoubleSide,
      }),
    );
  }
}
